import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";

// ── Types ──────────────────────────────────────────────────────────────────────

type ClientItem = {
  id: string;
  full_name: string;
  phone?: string | null;
  branch_id?: string | null;
};

type Branch = { id: string; name: string };
type Service = { id: string; name: string; duration_minutes: number };
type Slot = {
  staff_id: string;
  staff_name: string | null;
  start_at: string;
  end_at: string;
  score: number;
};

type LastAppointmentSummary = {
  appointment_id: string;
  branch_id: string;
  service_id: string;
  service_name?: string | null;
  staff_id?: string | null;
  staff_name?: string | null;
  status: string;
  start_at: string;
  end_at: string;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function isoDate(d: Date) {
  return d.toISOString().split("T")[0];
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function buildDateStrip(anchor: Date): Date[] {
  const days: Date[] = [];
  for (let i = -3; i <= 3; i++) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

// ── Step indicator ─────────────────────────────────────────────────────────────

function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex gap-1.5 items-center justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded-full transition-all",
            i + 1 === step ? "w-6 bg-teal-600" : i + 1 < step ? "w-3 bg-teal-400" : "w-3 bg-gray-200"
          )}
        />
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function TgBookPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const customerIdFromParam = searchParams.get("customer_id") ?? null;
  const customerNameFromParam = searchParams.get("customer_name") ?? "";
  const branchIdFromParam = searchParams.get("branch_id") ?? null;
  const serviceIdFromParam = searchParams.get("service_id") ?? null;
  const serviceNameFromParam = searchParams.get("service_name") ?? "";
  const repeatLastRequested = searchParams.get("repeat_last") === "1";
  const dayFromParam = searchParams.get("day") ? new Date(searchParams.get("day")!) : null;

  const totalSteps = customerIdFromParam ? 4 : 5;
  const stepOffset = customerIdFromParam ? 1 : 0; // skip step 1 if customer pre-filled

  const [step, setStep] = useState<number>(customerIdFromParam ? 2 : 1);
  const [customerId, setCustomerId] = useState<string | null>(customerIdFromParam);
  const [customerName, setCustomerName] = useState<string>(customerNameFromParam);
  const [branchId, setBranchId] = useState<string | null>(branchIdFromParam);
  const [serviceId, setServiceId] = useState<string | null>(serviceIdFromParam);
  const [serviceName, setServiceName] = useState<string>(serviceNameFromParam);
  const [selectedDay, setSelectedDay] = useState<Date>(dayFromParam ?? new Date());
  const [stripAnchor, setStripAnchor] = useState<Date>(dayFromParam ?? new Date());
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  const customerDetail = useQuery<ClientItem>({
    queryKey: ["tg-book-customer", customerId],
    queryFn: () => apiGet<ClientItem>(`/v3/customers/${customerId}`),
    enabled: !!customerId,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!customerDetail.data) return;
    if (!customerName) setCustomerName(customerDetail.data.full_name || "");
    if (!branchId && customerDetail.data.branch_id) setBranchId(customerDetail.data.branch_id);
  }, [branchId, customerDetail.data, customerName]);

  const repeatLastQuery = useQuery<{ item: LastAppointmentSummary | null }>({
    queryKey: ["tg-book-repeat-last", customerId],
    queryFn: () => apiGet<{ item: LastAppointmentSummary | null }>(`/v3/customers/${customerId}/last-appointment`),
    enabled: repeatLastRequested && !!customerId,
    staleTime: 30_000,
  });

  useEffect(() => {
    const last = repeatLastQuery.data?.item;
    if (!last) return;
    setBranchId(last.branch_id || branchIdFromParam);
    setServiceId(last.service_id);
    setServiceName(last.service_name || serviceNameFromParam || "");
  }, [branchIdFromParam, repeatLastQuery.data?.item, serviceNameFromParam]);

  // ── Step 1: Customer search ─────────────────────────────────────────────────

  const [clientSearch, setClientSearch] = useState("");
  const { data: clientsData, isLoading: clientsLoading } = useQuery<{ items: ClientItem[] }>({
    queryKey: ["tg-book-clients", clientSearch],
    queryFn: () => {
      const p = new URLSearchParams({ size: "20" });
      if (clientSearch) p.set("q", clientSearch);
      return apiGet(`/v3/tg/clients?${p}`);
    },
    enabled: step === 1,
  });

  // ── Step 2: Branch selection ────────────────────────────────────────────────

  const { data: branchesData, isLoading: branchesLoading } = useQuery<{ items: Branch[] }>({
    queryKey: ["tg-book-branches"],
    queryFn: () => apiGet("/v3/branches?size=50"),
    enabled: step === 2,
    staleTime: 60_000,
  });

  const branches = branchesData?.items ?? [];
  useEffect(() => {
    if (step !== 2) return;
    if (branchId && branches.some((b) => b.id === branchId)) {
      setStep(3);
      return;
    }
    if (!branchId && branches.length === 1) {
      setBranchId(branches[0].id);
      setStep(3);
    }
  }, [branchId, branches, step]);

  // ── Step 3: Service selection ───────────────────────────────────────────────

  const { data: servicesData, isLoading: servicesLoading } = useQuery<{ items: Service[] }>({
    queryKey: ["tg-book-services", branchId],
    queryFn: () => apiGet(`/v3/services?branch_id=${branchId}&size=100`),
    enabled: step === 3 && !!branchId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (step !== 3) return;
    const services = servicesData?.items ?? [];
    if (!services.length || !serviceId) return;
    const matched = services.find((s) => s.id === serviceId);
    if (!matched) {
      setServiceId(null);
      setServiceName("");
      return;
    }
    if (!serviceName) setServiceName(matched.name);
    setStep(4);
  }, [serviceId, serviceName, servicesData?.items, step]);

  // ── Step 4: Date + Slot ─────────────────────────────────────────────────────

  const dayStr = isoDate(selectedDay);
  const { data: availData, isLoading: availLoading } = useQuery<{ items: Slot[] }>({
    queryKey: ["tg-book-avail", branchId, serviceId, dayStr, customerId],
    queryFn: () => {
      const p = new URLSearchParams({ branch_id: branchId!, service_id: serviceId!, day: dayStr });
      if (customerId) p.set("customer_id", customerId);
      return apiGet(`/v3/tg/availability?${p}`);
    },
    enabled: step === 4 && !!branchId && !!serviceId,
  });

  const strip = buildDateStrip(stripAnchor);

  // ── Step 5: Confirm ─────────────────────────────────────────────────────────

  const displayStep = step - stepOffset;

  async function handleBook() {
    if (!customerId || !branchId || !serviceId || !selectedSlot) return;
    setIsBooking(true);
    setBookError(null);
    try {
      const result = await apiPost("/v3/tg/appointments", {
        branch_id: branchId,
        service_id: serviceId,
        staff_id: selectedSlot.staff_id,
        customer_id: customerId,
        start_at: selectedSlot.start_at,
        notes: notes || undefined,
      }) as { id: string; customer_notification_sent: boolean };
      queryClient.invalidateQueries({ queryKey: ["tg-today"] });
      queryClient.invalidateQueries({ queryKey: ["tg-calendar"] });
      if (result.customer_notification_sent) {
        // Brief toast-style feedback before navigating
        setBookError("✅ Confirmation sent to client");
        await new Promise((r) => setTimeout(r, 1200));
      }
      navigate(`/tg/appointments/${result.id}`);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "Booking failed";
      setBookError(msg);
    } finally {
      setIsBooking(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            if (step <= (customerIdFromParam ? 2 : 1)) {
              navigate(-1);
            } else {
              setStep(step - 1);
            }
          }}
          className="text-teal-600 text-sm font-medium"
        >
          ← Back
        </button>
        <h1 className="text-lg font-bold text-gray-900 flex-1">Assign Procedure</h1>
        <span className="text-xs text-gray-400">
          {displayStep}/{totalSteps}
        </span>
      </div>

      <StepIndicator step={displayStep} total={totalSteps} />

      {repeatLastRequested && (
        <div className="text-xs rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600">
          {repeatLastQuery.isLoading && "Loading last procedure..."}
          {!repeatLastQuery.isLoading && repeatLastQuery.data?.item && (
            <>Repeating: <span className="font-semibold">{repeatLastQuery.data.item.service_name ?? "Service"}</span></>
          )}
          {!repeatLastQuery.isLoading && !repeatLastQuery.data?.item && !repeatLastQuery.error && "No previous procedure found. Select one manually."}
          {repeatLastQuery.error && <span className="text-red-600">{String(repeatLastQuery.error)}</span>}
        </div>
      )}

      {/* Step 1: Select customer */}
      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Select client</p>
          <input
            type="search"
            placeholder="Search by name, phone…"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          {clientsLoading && <div className="text-gray-400 text-center py-4">Searching…</div>}
          <div className="space-y-2">
            {(clientsData?.items ?? []).map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setCustomerId(c.id);
                  setCustomerName(c.full_name);
                  if (c.branch_id) setBranchId(c.branch_id);
                  setStep(2);
                }}
                className="w-full text-left bg-white border border-gray-100 rounded-xl p-3 shadow-sm"
              >
                <p className="font-semibold text-gray-900">{c.full_name}</p>
                {c.phone && <p className="text-xs text-gray-500">{c.phone}</p>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Select branch */}
      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Select branch</p>
          {branchesLoading && <div className="text-gray-400 text-center py-4">Loading…</div>}
          <div className="space-y-2">
            {branches.map((b) => (
              <button
                key={b.id}
                onClick={() => {
                  setBranchId(b.id);
                  setStep(3);
                }}
                className="w-full text-left bg-white border border-gray-100 rounded-xl p-3 shadow-sm font-semibold text-gray-900"
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Select service */}
      {step === 3 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Select service</p>
          {servicesLoading && <div className="text-gray-400 text-center py-4">Loading…</div>}
          <div className="space-y-2">
            {(servicesData?.items ?? []).map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setServiceId(s.id);
                  setServiceName(s.name);
                  setStep(4);
                }}
                className="w-full text-left bg-white border border-gray-100 rounded-xl p-3 shadow-sm"
              >
                <p className="font-semibold text-gray-900">{s.name}</p>
                <p className="text-xs text-gray-500">{s.duration_minutes} min</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Date + slot */}
      {step === 4 && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-700">Pick date & time</p>

          {/* Date strip with prev/next */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setStripAnchor(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
              className="flex-shrink-0 px-2 py-2 text-gray-500 text-lg font-bold leading-none"
            >
              ‹
            </button>
            <div className="flex gap-1.5 overflow-x-auto pb-1 flex-1">
              {strip.map((d) => {
                const active = isoDate(d) === dayStr;
                return (
                  <button
                    key={isoDate(d)}
                    onClick={() => { setSelectedDay(d); setStripAnchor(d); setSelectedSlot(null); }}
                    className={cn(
                      "flex-shrink-0 flex flex-col items-center px-3 py-1.5 rounded-xl text-xs transition-colors",
                      active ? "bg-teal-600 text-white" : "bg-white border border-gray-200 text-gray-600"
                    )}
                  >
                    <span>{d.toLocaleDateString([], { weekday: "short" })}</span>
                    <span className="font-bold">{d.getDate()}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setStripAnchor(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
              className="flex-shrink-0 px-2 py-2 text-gray-500 text-lg font-bold leading-none"
            >
              ›
            </button>
          </div>

          {availLoading && <div className="text-gray-400 text-center py-4">Loading slots…</div>}

          {!availLoading && (availData?.items ?? []).length === 0 && (
            <div className="text-gray-400 text-center py-8">No available slots for this day.</div>
          )}

          {/* Slots grid */}
          <div className="grid grid-cols-3 gap-2">
            {(availData?.items ?? []).map((slot) => {
              const active = selectedSlot?.start_at === slot.start_at && selectedSlot?.staff_id === slot.staff_id;
              return (
                <button
                  key={`${slot.staff_id}:${slot.start_at}`}
                  onClick={() => setSelectedSlot(slot)}
                  className={cn(
                    "rounded-xl border p-2 text-xs text-center transition-colors",
                    active
                      ? "bg-teal-600 text-white border-teal-600"
                      : "bg-white border-gray-200 text-gray-700"
                  )}
                >
                  <p className="font-bold">{formatTime(slot.start_at)}</p>
                  <p className="truncate text-[10px] mt-0.5 opacity-80">{slot.staff_name ?? "Any"}</p>
                </button>
              );
            })}
          </div>

          {bookError && !bookError.startsWith("✅") && (
            <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{bookError}</div>
          )}

          <button
            disabled={!selectedSlot}
            onClick={() => setStep(5)}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-40 mt-2"
          >
            Continue
          </button>
        </div>
      )}

      {/* Step 5: Confirm */}
      {step === 5 && selectedSlot && (
        <div className="space-y-4">
          <p className="text-sm font-semibold text-gray-700">Confirm booking</p>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-2 text-sm">
            <Row label="Client" value={customerDetail.data?.full_name || customerName || "-"} />
            <Row label="Service" value={serviceName} />
            <Row label="Staff" value={selectedSlot.staff_name ?? "—"} />
            <Row label="Date" value={selectedDay.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })} />
            <Row label="Time" value={formatTime(selectedSlot.start_at)} />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Add a note…"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          {bookError && bookError.startsWith("✅") && (
            <div className="text-green-700 text-sm bg-green-50 rounded-lg p-3">{bookError}</div>
          )}
          {bookError && !bookError.startsWith("✅") && (
            <div className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{bookError}</div>
          )}

          <button
            onClick={handleBook}
            disabled={isBooking}
            className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-60"
          >
            {isBooking ? "Booking…" : "Create Booking"}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}
